import { useEffect, useState, useRef, useCallback, memo, lazy, Suspense } from 'react';
import { createLogger } from '@/lib/logger';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toolbar } from './toolbar';
import { MediaSidebar } from './media-sidebar';
import { PropertiesSidebar } from './properties-sidebar';
import { PreviewArea } from './preview-area';
import { ProjectDebugPanel } from './project-debug-panel';
import { Timeline } from '@/features/timeline/components/timeline';
import { ClearKeyframesDialog } from './clear-keyframes-dialog';
import { TemplateApplyDialog } from './template-apply-dialog';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { useEditorHotkeys } from '@/hooks/use-editor-hotkeys';
import { useAutoSave } from '../hooks/use-auto-save';
import { useTimelineShortcuts } from '@/features/timeline/hooks/use-timeline-shortcuts';
import { useTransitionBreakageNotifications } from '@/features/timeline/hooks/use-transition-breakage-notifications';
import { initTransitionChainSubscription } from '@/features/timeline/stores/transition-chain-store';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import { usePlaybackStore } from '@/features/preview/stores/playback-store';
import { cleanupBlobUrls } from '@/features/preview/utils/media-resolver';
import { useMediaLibraryStore } from '@/features/media-library/stores/media-library-store';
import { useProjectStore } from '@/features/projects/stores/project-store';
import { useEditorStore } from '../stores/editor-store';
import { useTemplateActions } from '../stores/useTemplateActions';
import { useSettingsStore } from '@/features/settings/stores/settings-store';
import { DEFAULT_TEMPLATE_ID } from '@/features/editor/data/templates';
import { collectVoiceoverTrimUpdates } from '@/lib/audio/trim-silence';
import { trimAudioSilence } from '@/features/timeline/stores/timeline-actions';
import { useItemsStore } from '@/features/timeline/stores/items-store';
import type { TimelineItem } from '@/types/timeline';

const logger = createLogger('Editor');
const LazyExportDialog = lazy(() =>
  import('@/features/export/components/export-dialog').then((module) => ({
    default: module.ExportDialog,
  }))
);
const LazyBundleExportDialog = lazy(() =>
  import('@/features/project-bundle/components/bundle-export-dialog').then((module) => ({
    default: module.BundleExportDialog,
  }))
);

function preloadExportDialog() {
  return import('@/features/export/components/export-dialog');
}

function preloadBundleExportDialog() {
  return import('@/features/project-bundle/components/bundle-export-dialog');
}

/** Project metadata passed from route loader (timeline loaded separately via loadTimeline) */
interface EditorProps {
  projectId: string;
  project: {
    id: string;
    name: string;
    width: number;
    height: number;
    fps: number;
    backgroundColor?: string;
    brandProfileId?: string;
    ctaConfig?: any;
    input_data?: any;
  };
}

/**
 * Video Editor Component
 * Memoized to prevent re-renders from route changes cascading to all children.
 */
/** Extra percentage to add to timeline panel when graph editor is open */
const GRAPH_PANEL_SIZE_INCREASE = 12; // ~12% extra height

export const Editor = memo(function Editor({ projectId, project }: EditorProps) {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [bundleExportDialogOpen, setBundleExportDialogOpen] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const { applyTemplate } = useTemplateActions();
  const [autoTrimmedProjectId, setAutoTrimmedProjectId] = useState<string | null>(null);
  const autoTrimSnapshotRef = useRef<{ projectId: string; items: TimelineItem[] } | null>(null);
  const isLoading = useTimelineStore((s) => s.timelineLoading);
  const timelineOpen = useEditorStore((s) => s.timelineOpen);
  const items = useTimelineStore((s) => s.items);
  const fps = useTimelineStore((s) => s.fps);
  const autoTrimVoiceoverSilence = useSettingsStore((s) => s.autoTrimVoiceoverSilence);

  // Guard against concurrent saves (e.g., spamming Ctrl+S)
  const isSavingRef = useRef(false);

  // Refs for imperative panel resizing
  const timelinePanelRef = useRef<ImperativePanelHandle>(null);
  const baseTimelineSizeRef = useRef(30); // Store the user's base timeline size

  // Initialize transition chain subscription (pre-computes chains from timeline data)
  // This subscription recomputes chains when items/transitions change
  useEffect(() => {
    const unsubscribe = initTransitionChainSubscription();
    return unsubscribe;
  }, []);

  // Initialize timeline from project data (or create default tracks for new projects)
  useEffect(() => {
    const { setCurrentProject: setMediaProject } = useMediaLibraryStore.getState();
    const { setCurrentProject } = useProjectStore.getState();
    const params = new URLSearchParams(window.location.search);
    const isIntegration = params.get('integration') === 'cenema-studio' || params.get('viewmode') === 'true';
    let cancelled = false;

    // Set current project context for media library (v3: project-scoped media)
    setMediaProject(projectId);

    // Set current project in project store for properties panel
    setCurrentProject({
      id: project.id,
      name: project.name,
      description: '',
      duration: 0,
      metadata: {
        width: project.width,
        height: project.height,
        fps: project.fps,
        backgroundColor: project.backgroundColor,
      },
      createdAt: Date.now(),
      updatedAt: Date.now(),
      brandProfileId: project.brandProfileId,
      ctaConfig: project.ctaConfig || project.input_data?.ctaConfig,
      input_data: project.input_data,
    });

    // Load timeline from IndexedDB - single source of truth for all timeline state
    const { loadTimeline } = useTimelineStore.getState();
    const initialize = async () => {
      try {
        await loadTimeline(projectId);

      } catch (error) {
        logger.error('Failed to initialize editor timeline:', error);
      }
    };

    void initialize();

    // Cleanup: clear project context, stop playback, and release blob URLs when leaving editor
    return () => {
      cancelled = true;
      useMediaLibraryStore.getState().setCurrentProject(null);
      useProjectStore.getState().setCurrentProject(null);
      usePlaybackStore.getState().pause();
      cleanupBlobUrls();
    };
  }, [projectId]); // Re-initialize when projectId changes

  useEffect(() => {
    autoTrimSnapshotRef.current = null;
    setAutoTrimmedProjectId(null);
  }, [projectId]);

  useEffect(() => {
    if (autoTrimVoiceoverSilence) return;

    const snapshot = autoTrimSnapshotRef.current;
    if (!snapshot || snapshot.projectId !== projectId) return;

    useItemsStore.getState().setItems(snapshot.items.map((item) => ({ ...item })));
    autoTrimSnapshotRef.current = null;
    setAutoTrimmedProjectId(null);
    toast.message('자동 무음 제거를 해제해 원본 타이밍으로 되돌렸습니다.');
  }, [autoTrimVoiceoverSilence, projectId]);

  useEffect(() => {
    if (!autoTrimVoiceoverSilence || isLoading) return;
    if (autoTrimmedProjectId === projectId) return;
    if (items.length === 0) return;

    let cancelled = false;

    const run = async () => {
      if (!autoTrimSnapshotRef.current || autoTrimSnapshotRef.current.projectId !== projectId) {
        autoTrimSnapshotRef.current = {
          projectId,
          items: items.map((item) => ({ ...item })),
        };
      }
      const updates = await collectVoiceoverTrimUpdates(items, fps);
      if (cancelled) return;
      if (updates.length > 0) {
        trimAudioSilence(updates);
        toast.success('보이스오버 무음을 자동 제거했습니다.');
      }
      setAutoTrimmedProjectId(projectId);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [autoTrimVoiceoverSilence, autoTrimmedProjectId, fps, isLoading, items, projectId]);

  // Track unsaved changes
  const isDirty = useTimelineStore((s: { isDirty: boolean }) => s.isDirty);

  // Save timeline to project (with guard against concurrent saves)
  const handleSave = useCallback(async () => {
    // Prevent concurrent saves (e.g., spamming Ctrl+S)
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSavingProject(true);
    const { saveTimeline } = useTimelineStore.getState();

    try {
      const savedProject = await saveTimeline(projectId);
      logger.debug('Project saved successfully');
      toast.success('Project saved');

      const { getMediaForProject } = await import('@/lib/storage/indexeddb/project-media');
      const projectAssets = await getMediaForProject(projectId) || [];

      // Notify parent about save success and provide updated data 
      // (Enables Cenema to sync back or update its state)
      const savePayload = {
        editor: 'superedit',
        projectId,
        projectName: project.name,
        timeline: savedProject.timeline, // Send full timeline for sync
        metadata: savedProject.metadata, // Width, height, fps, etc.
        brandProfileId: savedProject.brandProfileId,
        ctaConfig: savedProject.ctaConfig,
        input_data: savedProject.input_data,
        assets: projectAssets, // Full media list so it's not orphaned on reload
      };

      window.parent.postMessage({
        type: 'PROJECT_SAVED',
        payload: savePayload,
        data: savePayload // Compatibility with CenemaStudioSDK (destructures {type, data})
      }, '*');
    } catch (error) {
      logger.error('Failed to save project:', error);
      toast.error('Failed to save project');
      throw error; // Re-throw so callers know save failed
    } finally {
      isSavingRef.current = false;
      setIsSavingProject(false);
    }
  }, [projectId, project.name]);

  // Handle REQUEST_SAVE from parent (Cenema Host)
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'REQUEST_SAVE') {
        logger.debug('[Editor] Received REQUEST_SAVE from parent');
        await handleSave();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleSave]);

  const handleExport = useCallback(() => {
    // Pause playback when opening export dialog
    usePlaybackStore.getState().pause();
    void preloadExportDialog();
    setExportDialogOpen(true);
  }, []);

  const handleExportBundle = useCallback(() => {
    void preloadBundleExportDialog();
    setBundleExportDialogOpen(true);
  }, []);

  // Enable keyboard shortcuts
  useEditorHotkeys({
    onSave: handleSave,
    onExport: handleExport,
  });

  // Enable auto-save based on settings interval
  useAutoSave({
    isDirty,
    onSave: handleSave,
  });

  // Enable timeline shortcuts (space, cut tool, rate tool, etc.)
  useTimelineShortcuts();

  // Enable transition breakage notifications
  useTransitionBreakageNotifications();

  // TODO: Get actual timeline duration from project/timeline store
  const timelineDuration = 30; // 30 seconds placeholder

  // Track whether graph panel is currently open to avoid storing expanded size as base
  const isGraphOpenRef = useRef(false);

  // Handle graph panel open/close - resize timeline panel accordingly
  // Note: Resizing the graph panel via drag handle does NOT affect the overall timeline panel size.
  // Only opening/closing the graph panel changes the timeline panel size.
  const handleGraphPanelOpenChange = useCallback((isOpen: boolean) => {
    const panel = timelinePanelRef.current;
    if (!panel) return;

    if (isOpen && !isGraphOpenRef.current) {
      // Opening: store current size before expanding (only if not already open)
      baseTimelineSizeRef.current = panel.getSize();
      // Expand panel to accommodate graph editor
      const newSize = Math.min(50, baseTimelineSizeRef.current + GRAPH_PANEL_SIZE_INCREASE);
      panel.resize(newSize);
      isGraphOpenRef.current = true;
    } else if (!isOpen && isGraphOpenRef.current) {
      // Closing: restore to base size
      panel.resize(baseTimelineSizeRef.current);
      isGraphOpenRef.current = false;
    }
  }, []);

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Top Toolbar */}
      <Toolbar
        projectId={projectId}
        project={project}
        isDirty={isDirty}
        onSave={handleSave}
        onExport={handleExport}
        onExportBundle={handleExportBundle}
      />

      {/* Resizable Layout: Main Content + Timeline */}
      <ResizablePanelGroup direction="vertical" className="flex-1">
        {/* Main Content Area */}
        <ResizablePanel defaultSize={timelineOpen ? 70 : 100} minSize={timelineOpen ? 50 : 100} maxSize={100}>
          <div className="h-full flex overflow-hidden relative">
            {/* Left Sidebar - Media Library */}
            <ErrorBoundary level="feature">
              <MediaSidebar />
            </ErrorBoundary>

            {/* Center - Preview */}
            <ErrorBoundary level="feature">
              <PreviewArea project={project} />
            </ErrorBoundary>

            {/* Right Sidebar - Properties */}
            <ErrorBoundary level="feature">
              <PropertiesSidebar />
            </ErrorBoundary>
          </div>
        </ResizablePanel>

        {timelineOpen && <ResizableHandle withHandle />}

        {/* Bottom - Timeline */}
        {timelineOpen && (
          <ResizablePanel
            ref={timelinePanelRef}
            defaultSize={(new URLSearchParams(window.location.search).get('integration') === 'cenema-studio' || new URLSearchParams(window.location.search).get('viewmode') === 'true') ? 20 : 30}
            minSize={15}
            maxSize={50}
          >
            <ErrorBoundary level="feature">
              <Timeline
                duration={timelineDuration}
                onGraphPanelOpenChange={handleGraphPanelOpenChange}
              />
            </ErrorBoundary>
          </ResizablePanel>
        )}
      </ResizablePanelGroup>

      <Suspense fallback={null}>
        {/* Export Dialog */}
        {exportDialogOpen && (
          <LazyExportDialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} />
        )}

        {/* Bundle Export Dialog */}
        {bundleExportDialogOpen && (
          <LazyBundleExportDialog
            open={bundleExportDialogOpen}
            onClose={() => setBundleExportDialogOpen(false)}
            projectId={projectId}
            onBeforeExport={handleSave}
          />
        )}
      </Suspense>

      {/* Saving Dialog */}
      <Dialog open={isSavingProject} onOpenChange={() => { }} modal>
        <DialogContent
          className="sm:max-w-[300px] pointer-events-none"
          hideCloseButton
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex flex-col items-center justify-center space-y-3 pb-2 pt-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <DialogTitle className="text-center">Saving Project</DialogTitle>
            <DialogDescription className="text-center">
              Please wait while your project is saved...
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {/* Clear Keyframes Confirmation Dialog */}
      <ClearKeyframesDialog />

      {/* Template Apply Progress Dialog */}
      <TemplateApplyDialog />

      {/* Debug Panel (dev mode only) */}
      <ProjectDebugPanel projectId={projectId} />
    </div>
  );
});
